import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { ActivityAction, EntityType } from '@/types'

interface LogActivityParams {
  action: ActivityAction
  entityType?: EntityType
  entityId?: string | number
  details?: Record<string, unknown>
}

/**
 * Log an activity to the activity_logs table
 */
export async function logActivity({
  action,
  entityType,
  entityId,
  details,
}: LogActivityParams): Promise<void> {
  try {
    const store = useAuthStore.getState()
    const staffMember = store.staffMember

    const logEntry = {
      user_id: staffMember?.telegram_user_id || null,
      user_name: staffMember
        ? `${staffMember.first_name} ${staffMember.last_name || ''}`.trim()
        : 'Unknown',
      action,
      entity_type: entityType || null,
      entity_id: entityId ? String(entityId) : null,
      details: details || null,
    }

    const { error } = await supabase
      .from('activity_logs')
      .insert(logEntry as never)

    if (error) {
      console.error('Failed to log activity:', error)
    }
  } catch (error) {
    console.error('Activity logging error:', error)
  }
}

/**
 * Activity logging helper for common actions
 */
export const ActivityLogger = {
  // Auth events
  login: (userName: string) => logActivity({
    action: 'login',
    details: { user_name: userName }
  }),

  logout: (userName: string) => logActivity({
    action: 'logout',
    details: { user_name: userName }
  }),

  // Attendance events
  attendanceCorrection: (attendanceId: number, correctionType: string, staffName: string) => logActivity({
    action: 'update',
    entityType: 'attendance',
    entityId: attendanceId,
    details: { correction_type: correctionType, staff_name: staffName }
  }),

  attendanceCreate: (staffName: string) => logActivity({
    action: 'create',
    entityType: 'attendance',
    details: { staff_name: staffName }
  }),

  // Staff events
  staffCreate: (staffId: number, staffName: string) => logActivity({
    action: 'create',
    entityType: 'staff',
    entityId: staffId,
    details: { staff_name: staffName }
  }),

  staffUpdate: (staffId: number, staffName: string, changes: string[]) => logActivity({
    action: 'update',
    entityType: 'staff',
    entityId: staffId,
    details: { staff_name: staffName, changes }
  }),

  staffDelete: (staffId: number, staffName: string) => logActivity({
    action: 'delete',
    entityType: 'staff',
    entityId: staffId,
    details: { staff_name: staffName }
  }),

  // Inventory events
  productUpdate: (productId: number, productName: string, changes: Record<string, unknown>) => logActivity({
    action: 'update',
    entityType: 'product',
    entityId: productId,
    details: { product_name: productName, changes }
  }),

  csvImport: (recordCount: number) => logActivity({
    action: 'import',
    entityType: 'product',
    details: { record_count: recordCount }
  }),

  // Salary events
  salaryConfigUpdate: (staffId: number, staffName: string) => logActivity({
    action: 'update',
    entityType: 'salary',
    entityId: staffId,
    details: { staff_name: staffName }
  }),

  salaryPayment: (recordId: number, staffName: string, amount: number) => logActivity({
    action: 'create',
    entityType: 'payment',
    entityId: recordId,
    details: { staff_name: staffName, amount }
  }),

  // Announcement events
  announcementCreate: (announcementId: number, title: string) => logActivity({
    action: 'create',
    entityType: 'announcement',
    entityId: announcementId,
    details: { title }
  }),

  announcementUpdate: (announcementId: number, title: string) => logActivity({
    action: 'update',
    entityType: 'announcement',
    entityId: announcementId,
    details: { title }
  }),

  announcementDelete: (announcementId: number, title: string) => logActivity({
    action: 'delete',
    entityType: 'announcement',
    entityId: announcementId,
    details: { title }
  }),

  // Goal events
  goalCreate: (goalId: number, title: string) => logActivity({
    action: 'create',
    entityType: 'goal',
    entityId: goalId,
    details: { title }
  }),

  goalUpdate: (goalId: number, title: string, progress?: number) => logActivity({
    action: 'update',
    entityType: 'goal',
    entityId: goalId,
    details: { title, progress }
  }),

  goalDelete: (goalId: number, title: string) => logActivity({
    action: 'delete',
    entityType: 'goal',
    entityId: goalId,
    details: { title }
  }),

  // Export events
  dataExport: (dataType: string, recordCount: number) => logActivity({
    action: 'export',
    entityType: dataType as EntityType,
    details: { record_count: recordCount }
  }),

  // View events (for sensitive data)
  viewSensitiveData: (dataType: string, identifier?: string) => logActivity({
    action: 'view',
    entityType: dataType as EntityType,
    details: { identifier }
  }),
}

export default ActivityLogger
